import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { Box, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import connectionMonitor from "../../../utils/connectionMonitor";
import { getAuthToken } from "../../../utils/getAuthToken";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import {
  clearActiveBulkJob,
  isBulkJobRunning,
  restoreActiveBulkJob,
  selectActiveBulkJob,
  updateActiveBulkJob,
} from "../../../redux/Slice/Inbox/inboxBulkSelectionSlice";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Follows the Inbox bulk Close/Assign job the current user started (inboxBulkSelectionSlice
// activeJob) until it finishes, then shows the result. Progress arrives over the socket
// (inbox_bulk_action, sent only to this user); polling GET /api/inbox/bulk/:jobId is the fallback
// whenever the socket goes quiet (missed event, reconnect, socket server down) and after a page
// refresh (the job id is kept in sessionStorage for this tab). Always mounted in InboxAll.jsx —
// renders the progress strip that takes the filter row's place while a job runs.

const STORAGE_KEY = "inboxBulkActiveJob";
const POLL_MS = 3000;
const SOCKET_QUIET_MS = 4000;
// After this long still "waiting", the worker is most likely not running — say so instead of
// leaving a loader spinning (the job stays queued and finishes when the worker is back).
const QUEUED_TOO_LONG_MS = 20000;

const REASON_LABELS = {
  changed: "changed by someone else",
  already_closed: "already closed",
  already_assigned: "already assigned",
  not_open: "not open",
  blocked: "blocked",
  not_found: "not found",
  limit_reached: "plan limit reached",
  permission_denied: "no permission",
  performer_not_found: "no permission",
  invalid_target: "assignee unavailable",
  unknown_action: "not supported",
  error: "failed",
};

const plural = (n) => `${n} conversation${n === 1 ? "" : "s"}`;

// "2 conversations closed · 1 skipped (already closed)"
const buildResultMessage = (job, summary, results) => {
  const doneVerb = job.action === "assign" ? `assigned to ${job.targetName || "the selected assignee"}` : "closed";
  const reasonCounts = {};
  Object.values(results || {}).forEach((r) => {
    if (r.status === "done") return;
    const label = REASON_LABELS[r.reason] || "failed";
    reasonCounts[label] = (reasonCounts[label] || 0) + 1;
  });
  const others = Object.entries(reasonCounts).map(([label, n]) => `${n} ${label}`);
  const head = `${plural(summary?.done || 0)} ${doneVerb}`;
  return others.length ? `${head} · not changed: ${others.join(", ")}` : head;
};

const readStoredJob = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeStoredJob = (job) => {
  try {
    if (job) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(job));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable (private mode etc.) — refresh recovery just doesn't happen.
  }
};

const InboxBulkJobTracker = ({ showStrip }) => {
  const dispatch = useDispatch();
  const job = useSelector(selectActiveBulkJob);
  const running = isBulkJobRunning(job);
  const jobId = job?.jobId || null;
  const lastUpdateRef = React.useRef(0);
  const finishedRef = React.useRef(null);
  const [now, setNow] = React.useState(Date.now());

  // Restore a job started before a refresh.
  React.useEffect(() => {
    const stored = readStoredJob();
    if (stored?.jobId) dispatch(restoreActiveBulkJob({ ...stored, state: "waiting", processed: 0, startedAt: stored.startedAt || Date.now(), restored: true }));
  }, [dispatch]);

  // Keep the running job's identity in sessionStorage (cleared once it finishes).
  React.useEffect(() => {
    if (running && job) {
      writeStoredJob({ jobId: job.jobId, action: job.action, total: job.total, targetName: job.targetName, startedAt: job.startedAt || Date.now() });
    } else if (!job) {
      writeStoredJob(null);
    }
  }, [running, job]);

  const finish = React.useCallback((payload) => {
    if (!jobId || finishedRef.current === jobId) return;
    finishedRef.current = jobId;
    writeStoredJob(null);
    if (payload.state === "completed") {
      const summary = payload.summary || {};
      const message = buildResultMessage(job || {}, summary, payload.results);
      if ((summary.done || 0) > 0) toast.success(message, { id: `inbox-bulk-result-${jobId}`, duration: 6000 });
      else toast.error(message, { id: `inbox-bulk-result-${jobId}`, duration: 6000 });
    } else {
      toast.error(payload.message || "The bulk action stopped unexpectedly. Some conversations may not have been changed.", {
        id: `inbox-bulk-result-${jobId}`,
        duration: 6000,
      });
    }
    dispatch(clearActiveBulkJob());
  }, [dispatch, job, jobId]);

  const applyUpdate = React.useCallback((payload) => {
    if (!payload || payload.jobId !== jobId) return;
    lastUpdateRef.current = Date.now();
    if (payload.state === "completed" || payload.state === "failed") {
      finish(payload);
      return;
    }
    const nextState = payload.state === "progress" ? "active" : payload.state;
    dispatch(updateActiveBulkJob({ jobId, state: nextState, processed: payload.processed ?? 0 }));
  }, [dispatch, finish, jobId]);

  // Socket: progress/result pushed to this user only.
  React.useEffect(() => {
    if (!jobId) return undefined;
    const onUpdate = (payload) => applyUpdate(payload);
    connectionMonitor.on("inbox_bulk_action", onUpdate);
    return () => connectionMonitor.off("inbox_bulk_action", onUpdate);
  }, [jobId, applyUpdate]);

  // Polling fallback while the job runs and the socket has gone quiet.
  React.useEffect(() => {
    if (!running || !jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      setNow(Date.now());
      if (Date.now() - lastUpdateRef.current < SOCKET_QUIET_MS) return;
      try {
        const token = await getAuthToken();
        if (!token || cancelled) return;
        const { data } = await axios.get(`${SERVER_DOMAIN}/api/inbox/bulk/${encodeURIComponent(jobId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        applyUpdate({ ...data, jobId, processed: data.processed });
      } catch (err) {
        if (cancelled) return;
        // 404: the job no longer exists (expired, or not this user's) — stop tracking it.
        if (err.response?.status === 404) {
          writeStoredJob(null);
          dispatch(clearActiveBulkJob());
        }
      }
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [running, jobId, applyUpdate, dispatch]);

  if (!showStrip || !running || job.dismissed) return null;

  const total = job.total || 0;
  const processed = Math.min(job.processed || 0, total);
  const queuedTooLong = job.state === "waiting" && now - (job.startedAt || now) > QUEUED_TOO_LONG_MS;
  const verb = job.action === "assign" ? "Assigning" : "Closing";
  const label = queuedTooLong
    ? "Queued — will finish in the background"
    : `${verb} conversations${job.action === "assign" && job.targetName ? ` to ${job.targetName}` : ""}`;

  return (
    // Same height as the bulk action bar it follows (BULK_BAR_HEIGHT).
    <Box sx={{ pl: "12px", pr: "4px", height: 36, display: "flex", flexDirection: "column", justifyContent: "center", gap: "5px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}>
            {processed}/{total}
          </Typography>
          {queuedTooLong && (
            <Tooltip title="Hide — you'll still get the result" componentsProps={{ tooltip: { sx: DARK_BUBBLE_TOOLTIP_SX } }}>
              <IconButton
                size="small"
                aria-label="Hide bulk action progress"
                onClick={() => dispatch(updateActiveBulkJob({ jobId, dismissed: true }))}
                sx={{ p: 0.25, color: "text.secondary", "& .MuiSvgIcon-root": { fontSize: "14px" } }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <LinearProgress
        variant={processed === 0 ? "indeterminate" : "determinate"}
        value={total ? (processed / total) * 100 : 0}
        sx={{ height: 3, borderRadius: 2 }}
      />
    </Box>
  );
};

export default InboxBulkJobTracker;
